/**
 * AnchorNavigation module
 * Handles sticky anchor bar behavior with elevation effect
 * Used by pages with anchor navigation (CategoryPage, AboutUsCountryTemplate, etc.)
 */

export function initAnchorNavigation(containerSelector: string): void {
    const container = document.querySelector<HTMLElement>(containerSelector);

    if (!container) {
        return;
    }

    const anchorBarContainer = container.querySelector<HTMLElement>(".container__sticky--anchor");
    const anchorBar = anchorBarContainer?.querySelector<HTMLElement>(".anchor-bar");

    if (!anchorBarContainer || !anchorBar) {
        return;
    }

    // Get header element to read its real height dynamically
    const headerElement = document.querySelector<HTMLElement>('.js-header');
    const getHeaderHeight = (): number => headerElement?.getBoundingClientRect().height ?? 68;

    // Attention banner adds a fixed strip above the header; read its height from CSS variable
    const getAttentionBannerHeight = (): number =>
        parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--attention-banner-height')) || 0;

    // Sentinel must exist before createStickyObserver is called
    const sentinel = document.createElement('div');
    sentinel.style.height = '1px';
    sentinel.style.position = 'absolute';
    sentinel.style.width = '100%';
    sentinel.style.pointerEvents = 'none';
    anchorBarContainer.parentElement?.insertBefore(sentinel, anchorBarContainer);

    // IntersectionObserver is recreated whenever header height changes
    // so rootMargin always matches the current sticky top offset
    let stickyObserver: IntersectionObserver | null = null;
    const createStickyObserver = (headerHeight: number): void => {
        stickyObserver?.disconnect();
        const totalOffset = getAttentionBannerHeight() + headerHeight;
        stickyObserver = new IntersectionObserver(
            ([entry]) => {
                // When sentinel is NOT intersecting, the anchor bar is sticky
                if (!entry.isIntersecting) {
                    anchorBar.classList.add("is-sticky");
                } else {
                    anchorBar.classList.remove("is-sticky");
                }
            },
            {
                threshold: 0,
                rootMargin: `-${totalOffset}px 0px 0px 0px`
            }
        );
        stickyObserver.observe(sentinel);
    };

    // Apply sticky top offset: attention banner height + header height
    const updateStickyTop = (headerHeight: number): void => {
        anchorBarContainer.style.top = `${getAttentionBannerHeight() + headerHeight}px`;
    };

    // Watch for header height changes (e.g. when bottom bar shows/hides on scroll)
    // Use ResizeObserverEntry directly to avoid getBoundingClientRect forced reflow
    let resizeDebounceTimer: number | null = null;

    if (headerElement) {
        const headerResizeObserver = new ResizeObserver((entries) => {
            const height = entries[0]?.borderBoxSize?.[0]?.blockSize ?? entries[0]?.contentRect.height ?? 68;

            // Update top immediately so it tracks the CSS transition animation smoothly
            updateStickyTop(height);

            // Debounce observer recreation — only needs to fire once the animation settles
            if (resizeDebounceTimer !== null) window.clearTimeout(resizeDebounceTimer);
            resizeDebounceTimer = window.setTimeout(() => {
                createStickyObserver(height);
                resizeDebounceTimer = null;
            }, 150);
        });
        headerResizeObserver.observe(headerElement);
    }

    // Set initial top and observer using current header height
    const initialHeight = getHeaderHeight();
    updateStickyTop(initialHeight);
    createStickyObserver(initialHeight);

    // Get all anchor links
    const anchorLinks = anchorBar.querySelectorAll<HTMLAnchorElement>('a[href^="#"]');

    // Create a map of target sections
    const sectionMap = new Map<string, HTMLElement>();
    anchorLinks.forEach((link) => {
        const href = link.getAttribute('href');
        if (href && href !== '#') {
            const targetElement = document.querySelector<HTMLElement>(href);
            if (targetElement) {
                sectionMap.set(href, targetElement);
            }
        }
    });

    // Function to update active link based on scroll position
    const updateActiveLink = (): void => {
        const headerHeight = getHeaderHeight();
        const anchorBarHeight = anchorBar.offsetHeight;
        const scrollPosition = window.pageYOffset + getAttentionBannerHeight() + headerHeight + anchorBarHeight + 50; // Add small buffer

        let currentSection: string | null = null;

        // Find which section is currently in view
        sectionMap.forEach((section, href) => {
            const sectionTop = section.offsetTop;
            const sectionBottom = sectionTop + section.offsetHeight;

            if (scrollPosition >= sectionTop && scrollPosition < sectionBottom) {
                currentSection = href;
            }
        });

        // Update active state on links
        anchorLinks.forEach((link) => {
            const parentLi = link.closest('li');
            if (parentLi) {
                const href = link.getAttribute('href');
                if (href === currentSection) {
                    parentLi.classList.add('active');
                } else {
                    parentLi.classList.remove('active');
                }
            }
        });
    };

    // Listen to scroll events with throttling for performance
    let scrollTimeout: number | null = null;
    const handleScroll = (): void => {
        if (scrollTimeout !== null) {
            return;
        }
        scrollTimeout = window.setTimeout(() => {
            updateActiveLink();
            scrollTimeout = null;
        }, 100);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    // Initial check
    updateActiveLink();

    // Add smooth scroll for anchor links
    anchorLinks.forEach((link) => {
        link.addEventListener('click', (e: Event) => {
            e.preventDefault();

            const href = link.getAttribute('href');
            if (href && href !== '#') {
                const targetElement = sectionMap.get(href);

                if (targetElement) {
                    const anchorBarHeight = anchorBar.offsetHeight;
                    const bannerHeight = getAttentionBannerHeight();
                    const targetAbsoluteY = targetElement.getBoundingClientRect().top + window.pageYOffset;
                    const isScrollingDown = targetAbsoluteY > window.pageYOffset;

                    // Predict header height at destination:
                    // - scrolling DOWN → header will collapse to small size
                    // - scrolling UP or target near top → header will expand to full size
                    const style = getComputedStyle(document.documentElement);
                    const headerSmall = parseFloat(style.getPropertyValue('--menu__height--s')) || 60;
                    const headerFull  = parseFloat(style.getPropertyValue('--menu__height--md')) || 131;
                    const predictedHeaderHeight = isScrollingDown ? headerSmall : headerFull;

                    const offsetTop = targetAbsoluteY - bannerHeight - predictedHeaderHeight - anchorBarHeight;

                    window.scrollTo({ top: offsetTop, behavior: 'smooth' });

                    // Update URL hash without causing a jump
                    history.pushState(null, '', href);

                    // Update URL hash without causing a jump
                    history.pushState(null, '', href);

                    // Manually update active state immediately on click
                    anchorLinks.forEach((otherLink) => {
                        const parentLi = otherLink.closest('li');
                        if (parentLi) {
                            parentLi.classList.remove('active');
                        }
                    });

                    const parentLi = link.closest('li');
                    if (parentLi) {
                        parentLi.classList.add('active');
                    }
                }
            }
        });
    });
}
